import './playerRightTop.scss';
import React, { useContext, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Button, Dropdown, Space, Typography, Input, Tag } from "antd";
import { DownOutlined, DatabaseOutlined, FileTextOutlined, StarOutlined, StarFilled, SearchOutlined } from "@ant-design/icons";
import { PlayerContext } from "../../shared/contexts/playerContext";
import { DarkModeContext } from "../../shared/contexts/darkModeContext";

const { Text } = Typography;

const FAVORITES_KEY = 'player_menu_favorites';

const USER_DATA_LIST = [
    { key: '1', label: '아이템', uri: '/player/data/item' },
    { key: '2', label: '우편함', uri: '/player/data/mail' },
    { key: '3', label: '매장 정보', uri: '/player/data/store' },
    { key: '4', label: '캐릭터 정보', uri: '/player/data/character' },
    { key: '5', label: '미션 정보', uri: '/player/data/mission' },
    { key: '6', label: '도감/인연', uri: '/player/data/collection' },
    { key: '7', label: '패스/체인', uri: '/player/data/passChain' },
    { key: '8', label: '데이터', uri: '/player/data/playInfo' },
    { key: '9', label: '월정액', uri: '/player/data/monthly' },
    { key: '10', label: '머지 보드', uri: '/player/data/merge' },
    { key: '11', label: '머지 이벤트 보드', uri: '/player/data/mergeEvent'},
    { key: '12', label: '보스 레이드', uri: '/player/data/mergeEventBoss'},
    { key: '13', label: '가이드 미션', uri: '/player/data/guideMission'},
    { key: '14', label: '캐릭터 패키지(미니게임)', uri: '/player/data/characterPackage'},
    { key: '15', label: '친구 초대', uri: '/player/data/inviteFriend'},
    { key: '16', label: '손상된 데이터 확인', uri: "/player/data/corruptData"},
    { key: '17', label: '쇼핑로드', uri: "/player/data/shoppingRoad"},
    { key: '18', label: '트로피 챌린지', uri: "/player/data/trophy"},
    { key: '19', label: '마이드림 파트너', uri: "/player/data/myDreamPartner"},
    { key: '20', label: '캐릭터 카페', uri: '/player/data/characterCafe'},
    { key: '21', label: '두근두근 인형 뽑기', uri: '/player/data/claw'},
    { key: '22', label: '드림 벌룬 페스티벌', uri: '/player/data/balloon'},
    { key: '23', label: '트럭 이벤트', uri: '/player/data/truck'},
    { key: '24', label: '번영도', uri: '/player/data/prosperity'},
    { key: '25', label: '3일 연속 패키지', uri: '/player/data/threeDayStreak'},
    { key: '26', label: '프로필', uri: '/player/data/profile'},
    { key: '27', label: '트윈 룰렛', uri: '/player/data/twinRoulette'},
    { key: '28', label: '대청소 보드', uri: '/player/data/colorTarget'},
    { key: '300', label: '[임시]머지 이벤트 보상', uri: '/player/data/mergeEventBug'},
    { key: '400', label: '[임시]시즌 미션', uri: '/player/data/seasonMissionViewer'}
];

const LOG_LIST = [
    { key: '1', label: '로그인 로그', uri: '/player/log/login' },
    { key: '2', label: '결제 정보', uri: '/player/log/purchase' },
    { key: '3', label: '쿠폰 사용', uri: '/player/log/couponUse' },
    { key: '4', label: '우편 로그', uri: '/player/log/mail' },
    { key: '6', label: '재화 로그', uri: '/player/log/goods' },
    { key: '7', label: '가차 로그', uri: '/player/log/gacha' },
    { key: '8', label: '에러 로그', uri: '/player/log/error' },
    { key: '9', label: '파트너 로그', uri: '/player/log/partner' },
    { key: '10', label: '미라클 뱃지 로그', uri: '/player/log/miracleBadge' },
    { key: '11', label: '이벤트 트럭 로그', uri: '/player/log/eventTruck' },
    { key: '12', label: '럭키 아케이드 로그', uri: '/player/log/luckyArcade' },
    { key: '13', label: '프로필 로그', uri: '/player/log/profile' },
    { key: '14', label: '랭킹 대작전 로그', uri: '/player/log/rankingChallenge' },
    { key: '15', label: '광장 포인트 로그', uri: '/player/log/plazaPoint' },
    { key: '16', label: '코스튬 구매 로그', uri: '/player/log/costumePurchase' },
    { key: '17', label: '주사위 레이스 로그', uri: '/player/log/diceRace' },
];

const loadFavorites = () => {
    try {
        return JSON.parse(localStorage.getItem(FAVORITES_KEY)) || [];
    } catch {
        return [];
    }
};

const PlayerRightTop = () => {
    const { playerData } = useContext(PlayerContext);
    const { darkMode } = useContext(DarkModeContext);
    const [dataName, setDataName] = useState('유저 데이터');
    const [logName, setLogName] = useState('로그');
    const [dataOpen, setDataOpen] = useState(false);
    const [logOpen, setLogOpen] = useState(false);
    const [dataSearch, setDataSearch] = useState('');
    const [logSearch, setLogSearch] = useState('');
    const [favorites, setFavorites] = useState(loadFavorites);
    const navigate = useNavigate();
    const location = useLocation();

    const isActive = (path) => location.pathname.startsWith(path);
    const isDisabled = !playerData.userKey;

    const isFavorite = (type, key) => favorites.some(f => f.type === type && f.key === key);

    const toggleFavorite = (type, key, e) => {
        e.stopPropagation();
        e.preventDefault();
        const next = isFavorite(type, key)
            ? favorites.filter(f => !(f.type === type && f.key === key))
            : [...favorites, { type, key }];
        setFavorites(next);
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
    };

    const dragIndexRef = useRef(null);

    const handleDragStart = useCallback((favIndex) => {
        dragIndexRef.current = favIndex;
    }, []);

    const handleDragOver = useCallback((e, favIndex) => {
        e.preventDefault();
        const from = dragIndexRef.current;
        if (from === null || from === favIndex) return;
        const next = [...favorites];
        const [moved] = next.splice(from, 1);
        next.splice(favIndex, 0, moved);
        setFavorites(next);
        dragIndexRef.current = favIndex;
    }, [favorites]);

    const handleDrop = useCallback(() => {
        localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
        dragIndexRef.current = null;
    }, [favorites]);

    const handleDataItemClick = (item) => {
        navigate(item.uri);
        setDataName(item.label);
        setLogName('로그');
        setDataOpen(false);
        setDataSearch('');
    };

    const handleLogItemClick = (item) => {
        navigate(item.uri);
        setLogName(item.label);
        setDataName('유저 데이터');
        setLogOpen(false);
        setLogSearch('');
    };

    const handleFavoriteClick = (item) => {
        if (isDisabled) return;
        navigate(item.uri);
        if (item.favType === 'data') {
            setDataName(item.label);
            setLogName('로그');
        } else {
            setLogName(item.label);
            setDataName('유저 데이터');
        }
    };

    const filteredDataList = useMemo(
        () => USER_DATA_LIST.filter(item => item.label.includes(dataSearch)),
        [dataSearch]
    );

    const filteredLogList = useMemo(
        () => LOG_LIST.filter(item => item.label.includes(logSearch)),
        [logSearch]
    );

    const renderDropdown = useCallback((filteredList, search, setSearch, type, onItemClick) => (
        <div className={`custom-dropdown-menu ${darkMode ? 'dark' : 'light'}`}>
            <div className="dropdown-search-wrapper">
                <Input
                    prefix={<SearchOutlined />}
                    placeholder="검색"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    size="small"
                    allowClear
                    onClick={e => e.stopPropagation()}
                />
            </div>
            <div className="dropdown-items-list">
                {filteredList.length === 0 ? (
                    <div className="dropdown-no-result">결과 없음</div>
                ) : (
                    filteredList.map(item => (
                        <div
                            key={item.key}
                            className={`dropdown-menu-item ${location.pathname === item.uri ? 'is-current' : ''}`}
                            onClick={() => onItemClick(item)}
                        >
                            <span className="item-label">{item.label}</span>
                            <span
                                className={`item-star ${isFavorite(type, item.key) ? 'is-starred' : ''}`}
                                onClick={e => toggleFavorite(type, item.key, e)}
                            >
                                {isFavorite(type, item.key) ? <StarFilled /> : <StarOutlined />}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    ), [darkMode, favorites, location.pathname]);

    const favoriteItems = favorites
        .map((fav, index) => {
            const list = fav.type === 'data' ? USER_DATA_LIST : LOG_LIST;
            const item = list.find(i => i.key === fav.key);
            return item ? { ...item, favType: fav.type, favIndex: index } : null;
        })
        .filter(Boolean);

    return (
        <Card
            className={`player-right-top-card ${darkMode ? 'dark-theme' : 'light-theme'}`}
            styles={{
                header: {
                    borderBottom: `1px solid var(--border-color)`,
                    background: `var(--header-background)`,
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px'
                },
                body: {
                    padding: '20px 24px',
                    background: `var(--card-background)`
                }
            }}
            style={{
                borderRadius: '8px',
                border: `1px solid var(--border-color)`,
                background: `var(--card-background)`,
                boxShadow: 'var(--card-shadow)'
            }}
        >
            <div className="navigation-header">
                <Space align="center" size="middle">
                    <div className="nav-icon">
                        <DatabaseOutlined />
                    </div>
                    <div>
                        <Text strong style={{ color: 'var(--text-primary)' }}>플레이어 데이터 및 로그</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {isDisabled ? '플레이어를 먼저 검색해주세요' : '원하는 메뉴를 선택하세요'}
                        </Text>
                    </div>
                </Space>
            </div>

            {favoriteItems.length > 0 && (
                <div className="favorites-bar">
                    {favoriteItems.map(item => (
                        <Tag
                            key={`${item.favType}-${item.key}`}
                            className={`favorite-tag${isDisabled ? ' disabled' : ''}${location.pathname === item.uri ? ' active' : ''}`}
                            closable
                            onClose={e => { e.stopPropagation(); toggleFavorite(item.favType, item.key, e); }}
                            onClick={() => handleFavoriteClick(item)}
                            draggable
                            onDragStart={() => handleDragStart(item.favIndex)}
                            onDragOver={e => handleDragOver(e, item.favIndex)}
                            onDrop={handleDrop}
                        >
                            {item.label}
                        </Tag>
                    ))}
                </div>
            )}

            <div className="button-container">
                <Space size="large">
                    <Dropdown
                        open={dataOpen}
                        onOpenChange={(open) => {
                            setDataOpen(open);
                            if (!open) setDataSearch('');
                        }}
                        disabled={isDisabled}
                        placement="bottomLeft"
                        arrow
                        dropdownRender={() => renderDropdown(filteredDataList, dataSearch, setDataSearch, 'data', handleDataItemClick)}
                    >
                        <Button
                            size="large"
                            type={isActive('/player/data/') ? 'primary' : 'default'}
                            disabled={isDisabled}
                            className={`nav-button ${isActive('/player/data/') ? 'active' : ''}`}
                            icon={<DatabaseOutlined />}
                        >
                            <Space>
                                {dataName}
                                <DownOutlined />
                            </Space>
                        </Button>
                    </Dropdown>

                    <Dropdown
                        open={logOpen}
                        onOpenChange={(open) => {
                            setLogOpen(open);
                            if (!open) setLogSearch('');
                        }}
                        disabled={isDisabled}
                        placement="bottomLeft"
                        arrow
                        dropdownRender={() => renderDropdown(filteredLogList, logSearch, setLogSearch, 'log', handleLogItemClick)}
                    >
                        <Button
                            size="large"
                            type={isActive('/player/log/') ? 'primary' : 'default'}
                            disabled={isDisabled}
                            className={`nav-button ${isActive('/player/log/') ? 'active' : ''}`}
                            icon={<FileTextOutlined />}
                        >
                            <Space>
                                {logName}
                                <DownOutlined />
                            </Space>
                        </Button>
                    </Dropdown>
                </Space>
            </div>

            {isDisabled && (
                <div className="disabled-overlay">
                    <Text type="secondary" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                        플레이어 검색 후 이용 가능합니다
                    </Text>
                </div>
            )}
        </Card>
    );
};

export default PlayerRightTop;